import { z } from "zod";
import { AgentIdSchema } from "./primitives.js";
import { SessionHistoryDataSchema } from "./sessions.js";

const ScheduleSpecSchema = z.union([
  z.object({ type: z.literal("cron"), expr: z.string().min(1) }),
  z.object({ type: z.literal("every"), interval: z.string().min(1) }),
  z.object({ type: z.literal("at"), time: z.string().min(1) }),
]);

const ScheduleActiveHoursSchema = z.object({
  start: z.string(),
  end: z.string(),
});

const ConversationTargetSchema = z
  .object({ sessionKey: z.string(), sessionId: z.string() })
  .strict();

const ScheduledTaskStatusSchema = z.enum(["active", "paused", "completed", "cancelled", "errored"]);

const ScheduledTaskCommonSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  schedule: ScheduleSpecSchema,
  recurring: z.boolean(),
  agentId: z.string(),
  confirmationMode: z.enum(["manual", "bypass"]).optional(),
  createdBy: z.object({ agentId: z.string(), sessionKey: z.string() }),
  prompt: z.string().optional(),
  promptFile: z.string().optional(),
  activeHours: ScheduleActiveHoursSchema.optional(),
  timezone: z.string().min(1).optional(),
  status: ScheduledTaskStatusSchema,
  error: z.string().optional(),
  createdAt: z.number(),
  nextFireAt: z.number().optional(),
  lastFiredAt: z.number().optional(),
  triggeredAt: z.number().optional(),
  fireCount: z.number(),
  pausedAt: z.number().optional(),
  consecutiveFailures: z.number().optional(),
  maxRuns: z.number().optional(),
  expiresAt: z.number().optional(),
});

const ScheduledTaskSchema = ScheduledTaskCommonSchema.extend({
  type: z.literal("job"),
  conversationMode: z.enum(["task", "run"]),
  conversation: ConversationTargetSchema.optional(),
  notifyOnCompletion: ConversationTargetSchema.optional(),
});
export type ScheduledTaskView = z.infer<typeof ScheduledTaskSchema>;

export const ScheduleRunExecutionSchema = z.discriminatedUnion("state", [
  z.object({ state: z.literal("ready"), sessionId: z.string() }).strict(),
  z.object({ state: z.literal("unavailable") }).strict(),
]);
export type ScheduleRunExecutionView = z.infer<typeof ScheduleRunExecutionSchema>;
const ScheduleRunSchema = z.object({
  id: z.number(),
  scheduleId: z.string(),
  startedAt: z.number(),
  finishedAt: z.number().optional(),
  status: z.enum(["queued", "running", "paused", "completed", "failed", "cancelled"]),
  detail: z.string().optional(),
  output: z.string().optional(),
  durationMs: z.number().optional(),
  execution: ScheduleRunExecutionSchema,
  notificationStatus: z.enum(["pending", "accepted", "unavailable"]).optional(),
  notificationDetail: z.string().optional(),
  deliveryConversation: z
    .object({ sessionId: z.string(), sessionKey: z.string(), platform: z.string() })
    .strict()
    .optional(),
});
export type ScheduleRunView = z.infer<typeof ScheduleRunSchema>;

// ---------------------------------------------------------------------------
// schedule.list
// ---------------------------------------------------------------------------

export const ScheduleListPayloadSchema = z.object({ agentId: AgentIdSchema }).strict();
export type ScheduleListPayload = z.infer<typeof ScheduleListPayloadSchema>;

export const ScheduleListDataSchema = z.object({ tasks: z.array(ScheduledTaskSchema) });
export type ScheduleListData = z.infer<typeof ScheduleListDataSchema>;

// ---------------------------------------------------------------------------
// schedule.runs
// ---------------------------------------------------------------------------

export const ScheduleRunsPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    scheduleId: z.string().min(1),
    limit: z.number().int().positive().max(200).optional(),
  })
  .strict();
export type ScheduleRunsPayload = z.infer<typeof ScheduleRunsPayloadSchema>;

export const ScheduleRunsDataSchema = z.object({ runs: z.array(ScheduleRunSchema) });
export type ScheduleRunsData = z.infer<typeof ScheduleRunsDataSchema>;

export const ScheduleRunTranscriptDataSchema = SessionHistoryDataSchema;
export type ScheduleRunTranscriptData = z.infer<typeof ScheduleRunTranscriptDataSchema>;

const SchedulePatchSchema = z
  .object({
    confirmationMode: z.enum(["manual", "bypass"]).optional(),
    /** New cron / interval / ISO expression — reparsed and rescheduled. */
    schedule: z.string().min(1).optional(),
    prompt: z.string().min(1).optional(),
    promptFile: z.string().min(1).optional(),
    /** `null` clears the existing window; an object sets/replaces it. */
    activeHours: ScheduleActiveHoursSchema.nullable().optional(),
    /** `null` restores inheritance from the gateway default. */
    timezone: z.string().min(1).nullable().optional(),
    conversationMode: z.enum(["task", "run"]).optional(),
    notifyOnCompletion: z.string().min(1).nullable().optional(),
  })
  .strict();
export type SchedulePatch = z.infer<typeof SchedulePatchSchema>;

export const ScheduleUpdatePayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    id: z.string().min(1),
    patch: SchedulePatchSchema,
  })
  .strict();
export type ScheduleUpdatePayload = z.infer<typeof ScheduleUpdatePayloadSchema>;

export const ScheduleUpdateDataSchema = z.object({ task: ScheduledTaskSchema });
export type ScheduleUpdateData = z.infer<typeof ScheduleUpdateDataSchema>;

// ---------------------------------------------------------------------------
// schedule.action
// ---------------------------------------------------------------------------

export const ScheduleActionPayloadSchema = z
  .object({
    agentId: AgentIdSchema,
    id: z.string().min(1),
    action: z.enum(["pause", "resume", "cancel"]),
  })
  .strict();
export type ScheduleActionPayload = z.infer<typeof ScheduleActionPayloadSchema>;

export const ScheduleActionDataSchema = z.object({ task: ScheduledTaskSchema });
export type ScheduleActionData = z.infer<typeof ScheduleActionDataSchema>;
